import Phaser from 'phaser';
import {
  ClientMessage,
  isInsideDoor,
  PHYSICS,
  resolveLevel,
  ServerMessage,
  STAGE_1_4_ID,
  stage14GapWidth,
  type InputPayload,
  type LevelDef,
  type EmotePostedPayload,
  type SignObject,
  type StageClearPayload,
  type TerrainObject,
} from '@ponponland/shared';
import type { Room } from 'colyseus.js';
import { COLORS } from '../config/theme.js';
import { net } from '../net/client.js';
import { session } from '../state/session.js';
import type {
  BlockMap,
  BlockView,
  ButtonMap,
  ButtonView,
  PonView,
  PonMap,
  SpawnedTerrainArray,
  SpawnedTerrainView,
} from '../net/gameSchema.js';
import type { RoomSchema } from '../net/roomSchema.js';
import {
  mountTouchControls,
  showOnScreenControls,
  type TouchControlsHandle,
} from '../world/touchControls.js';
import { mountEmoteWheel, type EmoteWheelHandle } from '../world/emoteWheel.js';
import { preloadTileset, drawTiledBlock, drawDirtFill } from '../world/terrain.js';
import {
  preloadSceneBackdrop,
  drawSceneBackdrop,
  type SceneBackdropHandle,
} from '../world/backdrop.js';
import {
  preloadProps,
  addCrate,
  addDoor,
  addSign,
  createHintBubble,
  drawDecor,
  type DecorProp,
  type HintBubbleHandle,
} from '../world/props.js';
import { DEFAULT_TILESET } from '../config/tilesets.js';
import { STAGE_RENDER_PX } from '../world/scale.js';
import {
  createPonCharacter,
  preloadPonCharacters,
  type PonCharacter,
} from '../world/character/ponCharacter.js';
import { ponStateFrom } from '../world/character/ponState.js';
import { drawButtonKnob, drawParchmentBoard, drawParchmentBubble } from '../world/chrome.js';

/**
 * StageScene — the world kit's reference consumer (`client/src/world/README.md`
 * §"Composing a stage"): a GENERIC renderer that composes ANY server level def
 * from kit components only. Per level element: terrain rect → kit nine-slice
 * block (grass top aligned to the collision top), sign → kit sign prop + the
 * proximity hint bubble, block → kit crate (+ held glow), button → kit chrome
 * knob, door → kit wood door (+ synced glow/open), plus the kit backdrop, dirt
 * underfill, decor trees/bushes (placed clear of gameplay), kit Pon characters,
 * and the screen-fixed kit touch controls / emote wheel. No bespoke per-stage
 * drawing — every position comes from the server's level data.
 *
 * Networking is unchanged: server-authoritative physics state, pure
 * server-render with interpolation between the last two synced snapshots, and
 * movement **intent** sends only. The server keeps simulating the real levels.
 */

/**
 * Render pixels per gameplay tile — the kit's single scale source
 * (`world/scale.ts`): a Pon (`bodyDiameter` 1.5 tiles) renders at the plaza's
 * on-screen `PLAZA_PON_SIZE`, 1:1, no camera zoom. Client render scale only.
 */
const PX = STAGE_RENDER_PX;
/** Render this far (ms) behind the latest snapshot to interpolate smoothly. */
const INTERP_DELAY = 100;

interface PonSnap {
  x: number;
  y: number;
  vx: number;
  vy: number;
  grounded: boolean;
  facing: 'left' | 'right';
  life: string;
  grabbing: string;
  /** Absorb-chain pointers — drive the kit character's carrying/absorbed pose. */
  chainPrev: string;
  chainNext: string;
}

interface BlockSnap {
  x: number;
  y: number;
  w: number;
  h: number;
  grabbedBy: string;
}

interface Snapshot {
  /** Local receive time (ms). */
  t: number;
  pons: Map<string, PonSnap>;
  blocks: Map<string, BlockSnap>;
}

interface BlockSprite {
  container: Phaser.GameObjects.Container;
  /** Simple "being carried" glow, shown while held. */
  held: Phaser.GameObjects.Arc;
}

export class StageScene extends Phaser.Scene {
  private room!: Room<RoomSchema>;
  private level!: LevelDef;
  private sprites = new Map<string, PonCharacter>();
  /** Local-only: ms timestamp until which the local Pon shows the absorb channel. */
  private channelUntil = 0;
  private blockSprites = new Map<string, BlockSprite>();
  private snaps: Snapshot[] = [];
  /** Interpolated screen positions this frame (anchors the emote bubbles). */
  private screenPos = new Map<string, { x: number; y: number }>();
  /** Runtime-spawned bridge terrain layer — kit terrain blocks, drawn as synced. */
  private spawnedTerrainLayer!: Phaser.GameObjects.Container;
  private spawnedTerrainDrawn = 0;
  private backdrop: SceneBackdropHandle | null = null;
  private buttonSprites = new Map<string, { g: Phaser.GameObjects.Graphics; pressed: boolean }>();
  private doorGlow!: Phaser.GameObjects.Arc;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private keyA!: Phaser.Input.Keyboard.Key;
  private keyD!: Phaser.Input.Keyboard.Key;
  private keyW!: Phaser.Input.Keyboard.Key;
  private keySpace!: Phaser.Input.Keyboard.Key;
  private keyGrab!: Phaser.Input.Keyboard.Key;
  private keyRelease!: Phaser.Input.Keyboard.Key;
  private lastSent: InputPayload = { moveX: 0, jump: false };
  private touch: TouchControlsHandle | null = null;
  private touchOverlay: Phaser.GameObjects.DOMElement | null = null;
  private emoteWheel: EmoteWheelHandle | null = null;
  private emoteOverlay: Phaser.GameObjects.DOMElement | null = null;
  private emoteBubbles: {
    container: Phaser.GameObjects.Container;
    senderId: string;
    expires: number;
  }[] = [];
  private leaving = false;
  private clearPanel: Phaser.GameObjects.Container | null = null;
  /** The local player's proximity sign hint (kit bubble; nearest sign wins). */
  private hintBubble: HintBubbleHandle | null = null;
  private activeSignId = '';

  constructor() {
    super('Stage');
  }

  init(data: { stageId?: string }): void {
    this.level = resolveLevel(data?.stageId ?? '1-0');
  }

  preload(): void {
    const tileset = this.tileset;
    preloadTileset(this, tileset);
    preloadSceneBackdrop(this, tileset);
    preloadProps(this, tileset);
    preloadPonCharacters(this);
  }

  /** This stage's cosmetic tileset region (defaults to sunnyland). */
  private get tileset() {
    return this.level.tileset ?? DEFAULT_TILESET;
  }

  /**
   * Mirror the server's `GameRoom.scaledLevelFor` so the client renders
   * STAGE_1_4's party-scaled chasm geometry the server simulates. Both derive
   * from the shared `stage14GapWidth` curve so the rendered terrain/button/door
   * can never drift. Every other stage is returned unchanged.
   */
  private scaledLevelFor(level: LevelDef, playerCount: number): LevelDef {
    if (level.id !== STAGE_1_4_ID) return level;
    const LIP = 16;
    const gap = stage14GapWidth(playerCount);
    const perchLeft = LIP + gap;
    const [nearPath, basePerch] = level.terrain;
    const perchRight = basePerch.rect.x + basePerch.rect.w;
    const perch: TerrainObject = {
      type: 'terrain',
      rect: { x: perchLeft, y: basePerch.rect.y, w: perchRight - perchLeft, h: basePerch.rect.h },
    };
    const buttons = (level.buttons ?? []).map((b) => ({
      ...b,
      at: { x: perchLeft + 1.5, y: b.at.y },
      spawns: { x: LIP, y: b.spawns.y, w: gap, h: b.spawns.h },
    }));
    return {
      ...level,
      terrain: [nearPath, perch],
      buttons,
      door: { ...level.door, at: { x: perchLeft + 4.5, y: level.door.at.y } },
    };
  }

  create(): void {
    const room = net.game;
    if (!room) {
      this.scene.start('RoomBrowser');
      return;
    }
    this.room = room;
    this.level = this.scaledLevelFor(this.level, room.state?.players?.size ?? 0);
    this.leaving = false;
    this.snaps = [];
    this.sprites.clear();
    this.blockSprites.clear();
    this.buttonSprites.clear();
    this.spawnedTerrainDrawn = 0;
    this.backdrop = null;
    this.screenPos.clear();
    this.clearPanel = null;
    this.hintBubble = null;
    this.activeSignId = '';

    this.cameras.main.setBackgroundColor(COLORS.sky0);
    this.drawWorld();
    this.bindInput();
    this.mountTouch();
    this.mountEmoteWheel();

    this.room.onStateChange(() => this.onSnapshot());
    this.room.onMessage<StageClearPayload>(ServerMessage.StageClear, () => this.showClear());
    this.room.onMessage<EmotePostedPayload>(ServerMessage.EmotePosted, (m) => this.showEmote(m));
    this.room.onLeave(() => {
      if (!this.leaving) this.toLobbyOrBrowser();
    });
    this.room.onError(() => this.toLobbyOrBrowser());

    if (this.room.state) this.onSnapshot();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private teardown(): void {
    this.touch?.destroy();
    this.touch = null;
    this.touchOverlay?.destroy();
    this.touchOverlay = null;
    this.emoteWheel?.destroy();
    this.emoteWheel = null;
    this.emoteOverlay?.destroy();
    this.emoteOverlay = null;
    this.emoteBubbles.forEach((b) => b.container.destroy());
    this.emoteBubbles = [];
    this.room?.removeAllListeners();
  }

  // -- coordinate mapping (level tiles, y up → screen px, y down) -----------

  /** Ground top (level y=0) sits this many px from the canvas top. */
  private get groundScreenY(): number {
    return 360;
  }

  private sx(tileX: number): number {
    return tileX * PX;
  }

  private sy(tileY: number): number {
    return this.groundScreenY - tileY * PX;
  }

  /** Map a level `TileRect` (tiles, y up) to a kit `TiledBlock` (screen px). */
  private tiledBlockOf(rect: { x: number; y: number; w: number; h: number }) {
    return {
      left: this.sx(rect.x),
      top: this.sy(rect.y + rect.h),
      w: rect.w * PX,
      h: rect.h * PX,
    };
  }

  // -- world render (kit composition) ----------------------------------------

  private drawWorld(): void {
    const worldW = this.level.width * PX;
    const worldH = this.cameras.main.height;
    this.cameras.main.setBounds(0, -200, worldW, worldH + 400);

    // Sky underfill behind the kit backdrop so a panning camera never shows a gap.
    const g = this.add.graphics().setDepth(-20);
    g.fillGradientStyle(
      Phaser.Display.Color.HexStringToColor(COLORS.sky0).color,
      Phaser.Display.Color.HexStringToColor(COLORS.sky0).color,
      Phaser.Display.Color.HexStringToColor(COLORS.sky1).color,
      Phaser.Display.Color.HexStringToColor(COLORS.sky1).color,
      1
    );
    g.fillRect(-worldW, -500, worldW * 3, worldH + 1000);

    // Kit backdrop (the plaza's BG.png, parallax-scrolled).
    this.backdrop = drawSceneBackdrop(this, {
      worldW,
      worldH,
      groundY: this.groundScreenY,
      tileset: this.tileset,
    });

    // Kit terrain: deep dirt underfill below the ground plane + the server
    // level's platforms as plain grass-capped kit blocks (collision rects are
    // the server's; this is render only).
    const dirtLayer = this.add.container(0, 0).setDepth(-1.5);
    drawDirtFill(
      this,
      dirtLayer,
      { left: -worldW, top: this.sy(0), w: worldW * 3, h: worldH + 600 },
      this.tileset,
    );
    const terrainLayer = this.add.container(0, 0).setDepth(0);
    for (const t of this.level.terrain) {
      drawTiledBlock(this, terrainLayer, this.tiledBlockOf(t.rect), this.tileset);
    }
    // Kit decor (trees behind, bushes in front) rooted on terrain tops, placed
    // clear of every gameplay element. Behind terrain so platforms read solid.
    this.placeDecor();
    // Runtime-spawned bridge terrain (synced) — drawn as kit blocks as it appears.
    this.spawnedTerrainLayer = this.add.container(0, 0).setDepth(1);

    // Kit chrome buttons (pressed state synced from the server).
    for (const button of this.level.buttons ?? []) {
      const knob = this.add.graphics().setDepth(2);
      this.buttonSprites.set(button.id, { g: knob, pressed: false });
      drawButtonKnob(knob, this.sx(button.at.x), this.sy(button.at.y), false);
    }

    // Kit signs rooted on the surface their level `at.y` names, paired with one
    // shared proximity hint bubble (nearest in-range sign's copy, slice-4 §5).
    (this.level.signs ?? []).forEach((sign, i) => {
      addSign(this, {
        x: this.sx(sign.at.x),
        baseY: this.sy(sign.at.y),
        index: i,
        tileset: this.tileset,
      });
    });
    this.hintBubble = createHintBubble(this);

    // Kit door + glow, so the clear flow completes visibly.
    const d = this.level.door;
    this.doorGlow = this.add.circle(this.sx(d.at.x), this.sy(d.at.y), d.clearRadius * PX, 0xfff0b0, 0);
    addDoor(this, { x: this.sx(d.at.x), baseY: this.sy(d.at.y) });
  }

  /**
   * Decor trees/bushes (the plaza's props via the kit) rooted on terrain tops.
   * Generic: candidates step across the stage width; each roots on the highest
   * terrain surface under it (skipping gaps and below-main-plane pit floors) and
   * skips any spot near a gameplay element (sign/button/block/spawn/door) so
   * nothing important is obscured. Pure render — reads layout, never collides.
   */
  private placeDecor(): void {
    const terrain = this.level.terrain;
    if (terrain.length === 0) return;
    const mainTop = terrain[0].rect.y + terrain[0].rect.h;
    const avoidX: number[] = [this.level.door.at.x];
    for (const s of this.level.signs ?? []) avoidX.push(s.at.x);
    for (const b of this.level.buttons ?? []) avoidX.push(b.at.x);
    for (const b of this.level.blocks ?? []) avoidX.push(b.at.x + b.size.w / 2);
    for (const sp of this.level.spawns) avoidX.push(sp.at.x);
    const CLEAR = 2.2; // tiles of clearance around each gameplay element
    const clear = (tx: number): boolean => avoidX.every((a) => Math.abs(a - tx) > CLEAR);
    // Highest standable terrain top under a tile-x (null = gap / pit floor).
    const surfaceAt = (tx: number): number | null => {
      let top: number | null = null;
      for (const t of terrain) {
        if (tx < t.rect.x || tx > t.rect.x + t.rect.w) continue;
        const s = t.rect.y + t.rect.h;
        if (top === null || s > top) top = s;
      }
      return top !== null && top >= mainTop ? top : null;
    };

    const props: DecorProp[] = [];
    const STEP = 4.5; // tiles between candidate props
    for (let tx = 1.6, n = 0; tx < this.level.width - 1; tx += STEP, n += 1) {
      const top = surfaceAt(tx);
      if (top === null || !clear(tx)) continue;
      // Roots tuck just into the grass cap.
      props.push({ x: this.sx(tx), baseY: this.sy(top) + 6, height: n % 2 === 0 ? 150 : 120, kind: 'tree' });
    }
    for (let tx = 3.4; tx < this.level.width - 1; tx += STEP) {
      const top = surfaceAt(tx);
      if (top === null || !clear(tx)) continue;
      props.push({ x: this.sx(tx), baseY: this.sy(top) + 4, height: 34, kind: 'bush' });
    }
    const layer = this.add.container(0, 0).setDepth(-1);
    drawDecor(this, props, { layer, tileset: this.tileset });
  }

  // -- input ------------------------------------------------------------------

  private bindInput(): void {
    const kb = this.input.keyboard!;
    this.cursors = kb.createCursorKeys();
    this.keyA = kb.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyD = kb.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    this.keyW = kb.addKey(Phaser.Input.Keyboard.KeyCodes.W);
    this.keySpace = kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.keyGrab = kb.addKey(Phaser.Input.Keyboard.KeyCodes.E);
    this.keyRelease = kb.addKey(Phaser.Input.Keyboard.KeyCodes.R);
    this.keyGrab.on('down', () => {
      if (!this.leaving) this.triggerAbsorb();
    });
    this.keyRelease.on('down', () => {
      if (!this.leaving) this.room.send(ClientMessage.Release);
    });
  }

  /** Kit touch controls — same intents the keyboard produces, no new wire surface. */
  private mountTouch(): void {
    if (!showOnScreenControls()) return;
    const root = document.createElement('div');
    root.className = 'tc-overlay';
    // Screen-fixed: the follow-camera pans the world, never the control kit.
    this.touchOverlay = this.add.dom(0, 0, root).setOrigin(0, 0).setScrollFactor(0);
    this.touch = mountTouchControls({
      root,
      hint: '◀ ▶ run · ⤒ jump · Absorb',
      onGrab: () => {
        if (!this.leaving) this.triggerAbsorb();
      },
      onRelease: () => {
        if (!this.leaving) this.room.send(ClientMessage.Release);
      },
    });
  }

  /** Kit emote wheel (folded into the touch action cluster when present). */
  private mountEmoteWheel(): void {
    const inKit = this.touch !== null;
    this.emoteWheel = mountEmoteWheel({
      onPick: (callout) => {
        if (!this.leaving) this.room.send(ClientMessage.Emote, { emote: callout });
      },
      ...(inKit ? { size: 46 } : {}),
    });
    if (this.touch) {
      this.touch.actionCluster.insertBefore(
        this.emoteWheel.el,
        this.touch.actionCluster.firstChild,
      );
    } else {
      const root = document.createElement('div');
      root.className = 'tc-overlay';
      const cluster = document.createElement('div');
      cluster.className = 'tc-cluster ew-stage';
      root.appendChild(cluster);
      this.emoteOverlay = this.add.dom(0, 0, root).setOrigin(0, 0).setScrollFactor(0);
      cluster.appendChild(this.emoteWheel.el);
    }
  }

  /**
   * Send the Absorb intent + flash the local Pon's channel pose (the server
   * resolves the absorb in a single tick; the flash is local-only cosmetics).
   */
  private triggerAbsorb(): void {
    this.room.send(ClientMessage.Grab);
    this.channelUntil = performance.now() + 280;
  }

  private pollInput(): void {
    if (this.leaving) return;
    const t = this.touch?.state;
    const left = this.cursors.left.isDown || this.keyA.isDown || (t?.left ?? false);
    const right = this.cursors.right.isDown || this.keyD.isDown || (t?.right ?? false);
    const jump =
      this.cursors.up.isDown || this.keyW.isDown || this.keySpace.isDown || (t?.jump ?? false);
    const moveX: -1 | 0 | 1 = left && !right ? -1 : right && !left ? 1 : 0;
    this.touch?.clearJumpEdge();

    if (moveX !== this.lastSent.moveX || jump !== this.lastSent.jump) {
      this.lastSent = { moveX, jump };
      this.room.send(ClientMessage.Input, this.lastSent);
    }
  }

  // -- snapshots + interpolation ----------------------------------------------

  private onSnapshot(): void {
    if (this.room.state?.status === 'lobby' && !this.leaving) {
      // Server reset the room after a clear — rejoin the Lobby scene.
      this.toLobbyOrBrowser();
      return;
    }
    const game = this.room.state?.game;
    if (!game) return;
    const pons = new Map<string, PonSnap>();
    (game.pons as PonMap).forEach((p: PonView) => {
      pons.set(p.sessionId, {
        x: p.x,
        y: p.y,
        vx: p.vx,
        vy: p.vy,
        grounded: p.grounded,
        facing: p.facing,
        life: p.life,
        grabbing: p.grabbing ?? '',
        chainPrev: p.chainPrev ?? '',
        chainNext: p.chainNext ?? '',
      });
    });
    const blocks = new Map<string, BlockSnap>();
    (game.blocks as BlockMap).forEach((b: BlockView) => {
      blocks.set(b.id, { x: b.x, y: b.y, w: b.w, h: b.h, grabbedBy: b.grabbedBy ?? '' });
    });
    this.snaps.push({ t: performance.now(), pons, blocks });
    if (this.snaps.length > 12) this.snaps.shift();
  }

  update(): void {
    if (this.leaving) return;
    this.pollInput();
    this.renderInterpolated();
    this.renderSpawnedTerrain();
    this.renderButtons();
    this.renderDoor();
    this.renderSignHint();
    this.updateEmoteBubbles();
    this.followCamera();
    this.backdrop?.updateParallax();
  }

  /** Draw newly-synced spawned bridge terrain as kit blocks (append-only). */
  private renderSpawnedTerrain(): void {
    const game = this.room.state?.game;
    const spawned = game?.spawnedTerrain as SpawnedTerrainArray | undefined;
    if (!spawned || spawned.length === this.spawnedTerrainDrawn) return;
    this.spawnedTerrainLayer.removeAll(true);
    spawned.forEach((r: SpawnedTerrainView) => {
      drawTiledBlock(this, this.spawnedTerrainLayer, this.tiledBlockOf(r), this.tileset);
    });
    this.spawnedTerrainDrawn = spawned.length;
  }

  /** Repaint each kit button knob when its synced `pressed` latch flips. */
  private renderButtons(): void {
    const buttons = this.room.state?.game?.buttons as ButtonMap | undefined;
    if (!buttons) return;
    for (const button of this.level.buttons ?? []) {
      const state = buttons.get(button.id) as ButtonView | undefined;
      const sprite = this.buttonSprites.get(button.id);
      if (!state || !sprite || sprite.pressed === state.pressed) continue;
      sprite.g.clear();
      drawButtonKnob(sprite.g, this.sx(button.at.x), this.sy(button.at.y), state.pressed);
      sprite.pressed = state.pressed;
    }
  }

  private renderInterpolated(): void {
    if (this.snaps.length === 0) return;
    const renderT = performance.now() - INTERP_DELAY;

    let older = this.snaps[0];
    let newer = this.snaps[this.snaps.length - 1];
    for (let i = 0; i < this.snaps.length - 1; i++) {
      if (this.snaps[i].t <= renderT && this.snaps[i + 1].t >= renderT) {
        older = this.snaps[i];
        newer = this.snaps[i + 1];
        break;
      }
    }
    const span = newer.t - older.t;
    const f = span > 0 ? Phaser.Math.Clamp((renderT - older.t) / span, 0, 1) : 1;

    const seen = new Set<string>();
    this.screenPos.clear();
    newer.pons.forEach((nb, id) => {
      seen.add(id);
      const ob = older.pons.get(id) ?? nb;
      const px = this.sx(Phaser.Math.Linear(ob.x, nb.x, f));
      const py = this.sy(Phaser.Math.Linear(ob.y, nb.y, f));
      const sprite = this.ensureSprite(id);
      sprite.setPosition(px, py);
      sprite.setVisible(nb.life === 'alive');
      sprite.setFacing(nb.facing);
      sprite.setState(
        ponStateFrom({
          grabbing: nb.grabbing,
          chainPrev: nb.chainPrev,
          chainNext: nb.chainNext,
          moving: Math.abs(nb.vx) > 0.05,
          vy: nb.vy,
          grounded: nb.grounded,
          channeling: id === this.room.sessionId && performance.now() < this.channelUntil,
        }),
      );
      if (nb.life === 'alive') this.screenPos.set(id, { x: px, y: py });
    });
    for (const [id, sprite] of this.sprites) {
      if (!seen.has(id)) {
        sprite.destroy();
        this.sprites.delete(id);
      }
    }

    // Synced crates — the kit crate prop; a soft glow marks "being carried".
    const seenBlocks = new Set<string>();
    newer.blocks.forEach((nb, id) => {
      seenBlocks.add(id);
      const ob = older.blocks.get(id) ?? nb;
      const sprite = this.ensureBlockSprite(id, nb.w, nb.h);
      sprite.container.setPosition(
        this.sx(Phaser.Math.Linear(ob.x, nb.x, f)),
        this.sy(Phaser.Math.Linear(ob.y, nb.y, f)),
      );
      sprite.held.setVisible(nb.grabbedBy !== '');
    });
    for (const [id, sprite] of this.blockSprites) {
      if (!seenBlocks.has(id)) {
        sprite.container.destroy();
        this.blockSprites.delete(id);
      }
    }
  }

  private renderDoor(): void {
    const game = this.room.state?.game;
    if (!game) return;
    const d = this.level.door;
    let inside = 0;
    let total = 0;
    (game.pons as PonMap).forEach((p: PonView) => {
      total += 1;
      if (isInsideDoor(p.x, p.y, d.at.x, d.at.y, d.clearRadius)) inside += 1;
    });
    const ratio = total > 0 ? inside / total : 0;
    this.doorGlow.setFillStyle(game.doorOpen ? 0xffe9a8 : 0xfff0b0, game.doorOpen ? 0.85 : ratio * 0.55);
  }

  /**
   * Show the nearest in-range sign's copy in the kit hint bubble above the
   * LOCAL player's head; hide it when out of every sign's trigger radius. Pure
   * client HUD affordance (server-authoritative position, no game effect).
   */
  private renderSignHint(): void {
    const bubble = this.hintBubble;
    if (!bubble) return;
    const signs = this.level.signs ?? [];
    const me = this.room.sessionId;
    const pos = this.screenPos.get(me);
    const myPon = this.room.state?.game?.pons?.get(me) as PonView | undefined;
    if (signs.length === 0 || !pos || !myPon) {
      bubble.container.setVisible(false);
      this.activeSignId = '';
      return;
    }
    let best: SignObject | null = null;
    let bestD2 = Infinity;
    for (const sign of signs) {
      const dx = myPon.x - sign.at.x;
      const dy = myPon.y - sign.at.y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= sign.triggerRadius * sign.triggerRadius && d2 < bestD2) {
        bestD2 = d2;
        best = sign;
      }
    }
    if (!best) {
      bubble.container.setVisible(false);
      this.activeSignId = '';
      return;
    }
    if (best.id !== this.activeSignId) {
      this.activeSignId = best.id;
      bubble.setText(best.text);
    }
    bubble.container.setPosition(pos.x, pos.y - (PHYSICS.bodyDiameter * PX) / 2 - 28);
    bubble.container.setVisible(true);
  }

  private followCamera(): void {
    if (this.sprites.size === 0) return;
    let sum = 0;
    let n = 0;
    this.sprites.forEach((s) => {
      if (s.container.visible) {
        sum += s.container.x;
        n += 1;
      }
    });
    if (n === 0) return;
    this.cameras.main.centerOnX(sum / n);
    this.cameras.main.centerOnY(this.groundScreenY - 80);
  }

  // -- sprites ----------------------------------------------------------------

  private ensureSprite(id: string): PonCharacter {
    const existing = this.sprites.get(id);
    if (existing) return existing;
    const player = this.room.state?.players.get(id);
    const me = id === this.room.sessionId;
    const name = player?.name ?? 'Jelly';
    const sprite = createPonCharacter({
      scene: this,
      costume: player?.costume ?? 'classic',
      size: PHYSICS.bodyDiameter * PX,
      name: me ? `${name} (you)` : name,
    });
    this.sprites.set(id, sprite);
    return sprite;
  }

  private ensureBlockSprite(id: string, w: number, h: number): BlockSprite {
    const existing = this.blockSprites.get(id);
    if (existing) return existing;
    const pw = w * PX;
    const ph = h * PX;
    const held = this.add.circle(0, 0, Math.max(pw, ph) * 0.78, 0xffe9a8, 0.35);
    held.setStrokeStyle(4, 0xffc63a, 1);
    held.setVisible(false);
    const crate = addCrate(this, this.tileset).setDisplaySize(pw, ph);
    const container = this.add.container(0, 0, [held, crate]).setDepth(5);
    const sprite: BlockSprite = { container, held };
    this.blockSprites.set(id, sprite);
    return sprite;
  }

  // -- emotes -----------------------------------------------------------------

  /** Transient kit-parchment emote bubble tracking the sender's Pon. */
  private showEmote(m: EmotePostedPayload): void {
    const bg = this.add.graphics();
    const text = this.add
      .text(0, 0, m.emote, {
        fontFamily: 'Fredoka, sans-serif',
        fontSize: '16px',
        color: '#5a3a16',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 1);
    const pad = 8;
    const w = text.width + pad * 2;
    const h = text.height + 4;
    drawParchmentBubble(bg, -w / 2, -h, w, h, 8, false);
    text.setY(-3);
    const container = this.add.container(0, 0, [bg, text]).setDepth(950);
    container.setVisible(false);
    this.emoteBubbles.push({ container, senderId: m.senderId, expires: performance.now() + 1400 });
  }

  private updateEmoteBubbles(): void {
    if (this.emoteBubbles.length === 0) return;
    const now = performance.now();
    const r = (PHYSICS.bodyDiameter * PX) / 2;
    this.emoteBubbles = this.emoteBubbles.filter((b) => {
      if (now >= b.expires) {
        b.container.destroy();
        return false;
      }
      const pos = this.screenPos.get(b.senderId);
      if (pos) {
        b.container.setPosition(pos.x, pos.y - r - 16);
        b.container.setVisible(true);
      } else {
        b.container.setVisible(false);
      }
      return true;
    });
  }

  // -- clear + exit -----------------------------------------------------------

  private showClear(): void {
    if (this.clearPanel) return;
    const cam = this.cameras.main;
    const bg = this.add.graphics();
    drawParchmentBoard(bg, 0, 0, 360, 150, 14);
    const title = this.add
      .text(0, -22, 'Stage Clear!', {
        fontFamily: 'Fredoka, sans-serif',
        fontSize: '30px',
        color: '#8a5a10',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const sub = this.add
      .text(0, 24, 'Returning to the lobby…', {
        fontFamily: 'Fredoka, sans-serif',
        fontSize: '15px',
        color: '#7c6244',
      })
      .setOrigin(0.5);
    this.clearPanel = this.add
      .container(cam.midPoint.x, cam.midPoint.y, [bg, title, sub])
      .setDepth(1000);
    this.clearPanel.setScale(0.6);
    this.tweens.add({ targets: this.clearPanel, scale: 1, duration: 260, ease: 'Back.Out' });
  }

  /** The room reset us to the lobby phase — go back to the Lobby scene. */
  private toLobbyOrBrowser(): void {
    if (this.leaving) return;
    this.leaving = true;
    if (net.game && session.room) {
      this.scene.start('Lobby');
    } else {
      this.scene.start('RoomBrowser');
    }
  }
}
